'use client';

import * as React from 'react';
import Link from 'next/link';
import { CalendarIcon, PlayIcon } from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle
} from '@workspace/ui/components/dialog';
import { cn } from '@workspace/ui/lib/utils';

import { GridSection } from '~/components/fragments/grid-section';
import { SiteHeading } from '~/components/fragments/site-heading';

type ActiviteitenEnTakenHeroProps = {
  videoId?: string; // YouTube video ID
};

export function ActiviteitenEnTakenHero({
  videoId
}: ActiviteitenEnTakenHeroProps): React.JSX.Element {
  const [isVideoOpen, setIsVideoOpen] = React.useState(false);

  return (
    <GridSection>
      <div className="container space-y-10 py-24 md:py-36">
        {/* Badge */}
        <motion.div
          initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
          animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center"
        >
          <Badge
            variant="outline"
            className="h-8 rounded-full px-3 text-sm font-medium shadow-xs"
          >
            <CalendarIcon className="mr-2 size-4 shrink-0" />
            Activiteiten en taken
          </Badge>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <SiteHeading
            title="Nooit meer een taak vergeten of deadline missen"
            description="Met Emma beheer je moeiteloos al je activiteiten en taken. Van kleine herinneringen tot complexe projecten – houd alles bij op één centrale plek. Zo blijf je georganiseerd en productief, elke dag weer."
          />
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mx-auto flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:justify-center"
        >
          <Link
            href={routes.dashboard.auth.SignUp}
            className={cn(
              buttonVariants({
                variant: 'default'
              }),
              'h-10 rounded-xl sm:h-9'
            )}
          >
            Schrijf je nu in en start direct!
          </Link>
          <Link
            href={routes.marketing.Contact}
            className={cn(
              buttonVariants({
                variant: 'outline'
              }),
              'h-10 rounded-xl sm:h-9'
            )}
          >
            Neem contact op
          </Link>
        </motion.div>

        {/* YouTube Video */}
        {videoId ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.5 }}
            className="mx-auto mt-16 max-w-5xl"
          >
            <button
              type="button"
              onClick={() => setIsVideoOpen(true)}
              className="group relative block aspect-video w-full overflow-hidden rounded-xl border bg-muted transition-shadow hover:shadow-xl"
            >
              <img
                src="/assets/hero/activiteiten.png"
                alt="Video preview"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/30">
                <div className="flex size-20 items-center justify-center rounded-full bg-primary shadow-lg transition-transform group-hover:scale-110">
                  <PlayIcon
                    className="ml-1 size-10 text-white"
                    fill="currentColor"
                  />
                </div>
              </div>
            </button>

            <Dialog
              open={isVideoOpen}
              onOpenChange={setIsVideoOpen}
            >
              <DialogContent className="max-w-5xl p-0">
                <DialogTitle className="sr-only">
                  Activiteiten en Taken - Emma Demo
                </DialogTitle>
                <div className="relative aspect-video w-full">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title="Activiteiten en Taken - Emma Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>
        ) : null}
      </div>
    </GridSection>
  );
}
